// External Libraries
import React from "react";
import Modal from "react-responsive-modal";
import * as Icon from "react-feather";

// Services
import CommunicationService from "../../../services/CommunicationService";
import UtilityService from "../../../services/UtilityService";

// Common Components
import WComponent from "../../common/WComponent";
import TextFieldInput from "../../common/text_field_input/TextFieldInput";
import TextAreaField from "../../common/text_area_field/TextAreaField";
import DateSelector from "../../common/date_selector/DateSelector";
import Button from "../../common/button/Button";

// Constants
import COMMUNICATIONS from "../../../constants/Communications";

const { alert } = UtilityService;

const initialState = () => ({
	loading: false,
	communication: null,
	showPicker: true,

	type: "meeting",
	subject: "",
	rawContent: "",
	occurredAt: new Date(),
});

class CommunicationModal extends WComponent {
	constructor(props) {
		super(props);

		this.state = initialState();
	}

	componentDidUpdate = prevProps => {
		let { show, communicationId } = this.props;

		if (prevProps.communicationId !== communicationId || (prevProps.show === false && show === true)) {
			this.resetComponent();
		}
	};

	resetComponent = async () => {
		let { communicationId } = this.props;

		await this.update(initialState());

		if (communicationId) {
			await this.fetchCommunication();
		}
	};

	fetchCommunication = async () => {
		let { communicationId } = this.props;

		await this.update({ loading: true });

		try {
			const result = await CommunicationService.getCommunication({ communicationId });

			if (result) {
				await this.update({ communication: result });
			}
		} catch (error) {
			console.log(error);
		}

		await this.update({ loading: false });
	};

	onChange = e => {
		this.update({ [e.target.name]: e.target.value });
	};

	onDateSelect = date => {
		this.update({ occurredAt: date });
	};

	onTypeSelect = type => {
		this.update({ type });
	};

	onSave = async () => {
		let { clientId } = this.props;
		let { type, subject, rawContent, occurredAt } = this.state;

		if (!subject.trim()) {
			alert({ type: "error", text: "Please enter a subject." });
			return;
		}

		// Synchronous guard: `loading` is set asynchronously via update(), so a fast
		// double-click can pass a state-based check twice and create two rows.
		if (this.saving) return;
		this.saving = true;

		await this.update({ loading: true });

		try {
			await CommunicationService.createCommunication({
				clientId,
				type,
				source: "manual",
				subject: subject.trim(),
				rawContent: rawContent.trim(),
				occurredAt: occurredAt ? occurredAt.toISOString() : new Date().toISOString(),
			});

			alert({ type: "success", text: "Communication logged. AI summary is being generated..." });

			if (this.props.onClose) {
				this.props.onClose();
			}
		} catch (error) {
			console.log(error);
			alert({ type: "error", text: "Failed to save. Please try again." });
		}

		this.saving = false;
		await this.update({ loading: false });
	};

	onDelete = async () => {
		let { communicationId } = this.props;

		await this.update({ loading: true });

		try {
			await CommunicationService.deleteCommunication({ communicationId });
			alert({ type: "success", text: "Communication deleted." });

			if (this.props.onClose) {
				this.props.onClose();
			}
		} catch (error) {
			console.log(error);
			alert({ type: "error", text: "Failed to delete." });
		}

		await this.update({ loading: false });
	};

	onClose = () => {
		if (this.props.onClose) {
			this.props.onClose();
		}
	};

	isViewMode = () => {
		return !!this.props.communicationId;
	};

	onPickLogManually = () => {
		this.update({ showPicker: false });
	};

	onPickImportFromMeet = () => {
		this.onClose();
		if (this.props.onImportFromMeet) {
			this.props.onImportFromMeet();
		}
	};

	renderPickerMode = () => {
		return (
			<div className="comm-modal__picker">
				<div className="comm-modal__picker__option" onClick={this.onPickLogManually}>
					<div className="comm-modal__picker__option__icon">
						<Icon.Edit3 size={32} strokeWidth={1.5} />
					</div>
					<div className="comm-modal__picker__option__title">Log Manually</div>
					<div className="comm-modal__picker__option__desc">
						Type or paste your own notes for a call, email, or meeting.
					</div>
				</div>

				<div className="comm-modal__picker__option" onClick={this.onPickImportFromMeet}>
					<div className="comm-modal__picker__option__icon">
						<Icon.Video size={32} strokeWidth={1.5} />
					</div>
					<div className="comm-modal__picker__option__title">Import from Google Meet</div>
					<div className="comm-modal__picker__option__desc">
						Select a transcript from your Meet Recordings folder and summarise it with AI.
					</div>
				</div>
			</div>
		);
	};

	renderViewMode = () => {
		let { communication } = this.state;

		if (!communication) {
			return <div className="comm-modal__loading">Loading...</div>;
		}

		const { type, source, subject, occurred_at, participants, status, drive_file_url, raw_content, summary } = communication;
		const dateLabel = UtilityService.formatDate({ value: occurred_at, format: "MMMM D, YYYY" });
		const participantList = Array.isArray(participants) ? participants : [];

		return (
			<div className="comm-modal__view">
				<div className="comm-modal__view__meta">
					<span className={`comm-card__type comm-card__type--${type}`}>{type}</span>
					<span className="comm-modal__view__date">{dateLabel}</span>
				</div>

				{subject && <div className="comm-modal__view__subject">{subject}</div>}

				{participantList.length > 0 && (
					<div className="comm-modal__view__participants">
						<Icon.Users size={14} />
						<span>{participantList.map(p => p.name || p.email).join(", ")}</span>
					</div>
				)}

				{status !== "done" && (
					<div className={`comm-card__status comm-card__status--${status}`}>
						{COMMUNICATIONS.statusLabels[status] || status}
					</div>
				)}

				{status === "done" && summary && (
					<div className="comm-modal__view__summary">
						{summary.category && <div className="comm-modal__view__category">{summary.category}</div>}

						<div className="comm-modal__view__summary__label">AI Summary</div>
						<div className="comm-modal__view__summary__text">{summary.summary}</div>

						{Array.isArray(summary.action_items) && summary.action_items.length > 0 && (
							<div className="comm-card__action-items">
								<div className="comm-card__action-items__label">Action Items</div>
								<ul className="comm-card__action-items__list">
									{summary.action_items.map((item, i) => (
										<li key={i}>{item}</li>
									))}
								</ul>
							</div>
						)}
					</div>
				)}

				{raw_content && (
					<div className="comm-modal__view__raw">
						<div className="comm-modal__view__raw__label">Original Notes</div>
						<div className="comm-modal__view__raw__text">{raw_content}</div>
					</div>
				)}

				{drive_file_url && (
					<div className="comm-modal__view__drive" onClick={() => window.open(drive_file_url, "_blank")}>
						<Icon.ExternalLink size={14} />
						View Full Notes in Drive
					</div>
				)}
			</div>
		);
	};

	renderCreateMode = () => {
		let { type, subject, rawContent, occurredAt } = this.state;

		return (
			<div className="comm-modal__create">
				<div className="comm-modal__create__types">
					{COMMUNICATIONS.typeOptions.map(opt => (
						<div
							key={opt.id}
							className={`comm-modal__create__type${type === opt.id ? " comm-modal__create__type--active" : ""}`}
							onClick={() => this.onTypeSelect(opt.id)}
						>
							{opt.label}
						</div>
					))}
				</div>

				<TextFieldInput title="Subject" name="subject" onChange={this.onChange} value={subject} autoComplete="off" />

				<DateSelector title="Date" selectedDate={occurredAt} onDateSelect={this.onDateSelect} />

				<TextAreaField
					title="Notes"
					name="rawContent"
					onChange={this.onChange}
					value={rawContent}
					placeholder="Paste or type the meeting notes, call summary, or any relevant details..."
				/>
			</div>
		);
	};

	render() {
		let { show } = this.props;
		let { loading, showPicker } = this.state;

		const viewMode = this.isViewMode();
		const isPicker = !viewMode && showPicker;

		let title = "Communication";
		if (!viewMode) title = isPicker ? "New Communication" : "Log Manually";

		return (
			<Modal classNames={{ modal: "comm-modal" }} open={show} onClose={this.onClose} center>
				<div className="comm-modal__header">
					<div className="comm-modal__header__title">{title}</div>
				</div>

				<div className="comm-modal__body">
					{viewMode && this.renderViewMode()}
					{!viewMode && isPicker && this.renderPickerMode()}
					{!viewMode && !isPicker && this.renderCreateMode()}
				</div>

				{!isPicker && (
					<div className="comm-modal__footer">
						{viewMode && (
							<Button view="secondary" icon={<Icon.Trash2 size={18} />} onClick={this.onDelete} disabled={loading} />
						)}
						{viewMode && <Button view="secondary" text="Close" onClick={this.onClose} />}
						{!viewMode && <Button view="secondary" text="Cancel" onClick={this.onClose} />}
						{!viewMode && <Button text="Save & Summarize" onClick={this.onSave} disabled={loading} />}
					</div>
				)}
			</Modal>
		);
	}
}

export default CommunicationModal;
